import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isRecapContentUnavailable, listCoachRecaps } from '@/lib/api';
import { coachRecapCollectionKey, queryClient } from '@/lib/queryClient';
import type { RecapCollectionResponse, RecapScope } from '@/lib/types';
import { useRecapBoundary } from './useCanonicalRecap';

export type CoachRecapRow = RecapCollectionResponse['items'][number];
/** Only a complete, contiguous snapshot can support local filtering, bulk
 * selection or an assertion that a match has no recap. Cache entries remain
 * individual server pages; the aggregate belongs to this read lifetime. */
export function useCoachRecaps(scope: RecapScope) {
  const boundary = useRecapBoundary(scope);
  const [denied, setDenied] = useState(false);
  const readPage = async (query: { limit: number; cursor?: string }, signal: AbortSignal) => {
    try { return await listCoachRecaps(query, { scope, signal }); }
    catch (error) {
      // The shared API evicts every list page on an authoritative denial.
      // Preserve that event outside the evicted Query's transient state.
      if (boundary.current() && isRecapContentUnavailable(error)) setDenied(true);
      throw error;
    }
  };
  const first = useQuery({ queryKey: coachRecapCollectionKey(scope, { limit: 50 }),
    queryFn: ({ signal }) => readPage({ limit: 50 }, signal),
    enabled: boundary.active && !denied && scope.view === 'coach', staleTime: 0, retry: false, refetchOnMount: 'always' });
  const [result, setResult] = useState<{ first: RecapCollectionResponse; items: CoachRecapRow[]; error: boolean } | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!boundary.active || denied || !first.isFetchedAfterMount || first.isFetching || !first.data || first.isError) return;
    let live = true; const readKeys: ReturnType<typeof coachRecapCollectionKey>[] = [], pageOne = first.data;
    setResult(null);
    void (async () => {
      const items: CoachRecapRow[] = [], ids = new Set<string>(), cursors = new Set<string>();
      let page = pageOne;
      try {
        for (;;) {
          if (!live || !boundary.current()) return;
          if (page.view !== 'coach' || page.snapshot !== pageOne.snapshot || page.total !== pageOne.total || page.limit !== 50
            || page.range_start !== (page.items.length ? items.length + 1 : 0)
            || page.range_end !== (page.items.length ? items.length + page.items.length : 0)
            || items.length + page.items.length > page.total) throw Error('Recap snapshot changed');
          for (const row of page.items) { if (ids.has(row.recap_id)) throw Error('Repeated recap'); ids.add(row.recap_id); items.push(row); }
          if (!page.next_cursor) {
            if (items.length !== page.total) throw Error('Incomplete recap snapshot');
            setResult({ first: pageOne, items, error: false }); return;
          }
          if (!page.items.length || cursors.has(page.next_cursor)) throw Error('Repeated recap cursor');
          cursors.add(page.next_cursor); const query = { limit: 50, cursor: page.next_cursor };
          const key = coachRecapCollectionKey(scope, query); readKeys.push(key);
          // Own a fresh page read, never a still-running read from a prior
          // aggregate. The API continues to own cursor and scope admission.
          await queryClient.cancelQueries({ queryKey: key, exact: true }, { revert: false });
          if (!live || !boundary.current()) return;
          page = await queryClient.fetchQuery({ queryKey: key, staleTime: 0, retry: false,
            queryFn: ({ signal }) => readPage(query, signal) });
        }
      } catch {
        if (live && boundary.current()) setResult({ first: pageOne, items: [], error: true });
      }
    })();
    return () => { live = false; readKeys.forEach(queryKey => { void queryClient.cancelQueries({ queryKey, exact: true }, { revert: false }); }); };
  }, [boundary.active, denied, first.data, first.isFetchedAfterMount, first.isFetching, first.isError, attempt]);
  const admitted = boundary.active && !denied && first.isFetchedAfterMount && !first.isFetching && !first.isError && result?.first === first.data;
  const complete = !!(admitted && !result?.error);
  return { active: boundary.active, complete, items: complete ? result!.items : [],
    error: boundary.active && (denied || first.isError || !!(admitted && result?.error)),
    refresh: async () => { setDenied(false); setResult(null); await first.refetch(); setAttempt(value => value + 1); },
  };
}
